import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { DataDiagramField } from './DataDiagramField';
import { DiagramDisplayData } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData';
import { Tree } from 'components/Tree/CommonTree/Tree';
import { DataDescription } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { useCreateTreeStore } from 'components/Tree/CommonTree/useTreeStore';
import { isNotEmpty } from 'utils/ArrayUtils';
import './DataDiagram.less';
import { ValidationIcon } from 'domains/designers/common';
import { useDidUpdateEffect } from 'hooks/useDidUpdateEffect';
import { SimpleDiagramHeight } from './DataDesignerUtils';
import { generateGuid } from 'utils/Guid';
import { ITreeNode, ITreeNodeContentProps, TreeNodeActionVisiblity } from 'components/Tree/CommonTree/CommonTreeNode';

export type DataDiagramMouseEvent = React.MouseEvent<HTMLElement> & { diagram: DiagramDisplayData };

export interface IDiagramTreeNode {
  dataDescription: DataDescription;
  path: string;
  isRoot: boolean;
}

export interface IDataDiagramProps {
  diagramData: DiagramDisplayData;
  onClick: (data: DiagramDisplayData) => void;
  onMouseDown: (e: DataDiagramMouseEvent) => void;
  getDataFields: (id: string, path: string) => Promise<DataDescription[]>;
  onNameChanged: (id: string, newName: string) => void;
  onRootNodeExpand: (id: string, isExpanded: boolean) => void;
}

function getClassNames(diagram: DiagramDisplayData) {
  return classNames('dp-data-diagram', {
    'dp-data-diagram--simple': diagram.SubDataDesriptions == null || diagram.SubDataDesriptions.length === 0,
    'dp-data-diagram--list': diagram.DataDescription.IsList,
    'dp-data-diagram--complex': isNotEmpty(diagram.SubDataDesriptions),
  });
}

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export const DataDiagram = React.memo(
  ({ diagramData, onClick, onMouseDown, getDataFields, onNameChanged, onRootNodeExpand }: IDataDiagramProps) => {
    const [id] = useState<string>(generateGuid());

    const loadNodes = useCallback(
      async (parent?: ITreeNode<IDiagramTreeNode>) => {
        if (parent) {
          return await getDataFields(diagramData.Id, parent.data!.path).then(fields =>
            (fields ?? []).map(item => convertToNode(item, parent.data!.path))
          );
        } else {
          return getTreeModel(diagramData);
        }
      },
      [diagramData, getDataFields]
    );

    const useTreeStore = useCreateTreeStore<IDiagramTreeNode>({
      treeId: id,
      nodes: getTreeModel(diagramData),
      loadNodes,
      onNodeExpanded: node => {
        if (node.data!.isRoot) {
          onRootNodeExpand(node.nodeId, true);
        }
      },
      onNodeFolded: node => {
        if (node.data!.isRoot) {
          onRootNodeExpand(node.nodeId, false);
        }
      },
    });

    const nodes = useTreeStore(p => p.nodes);
    const { expandNode, foldNode, replaceNodes } = useTreeStore(p => p.actions);

    useDidUpdateEffect(() => {
      // Each node has "path" which we use to get sub-tree.
      // When we change diagram name, all pathes are not relevant, so we need to reset model.
      // TODO: Right now, It causes collapsing all nodes, in the future we need path throw all nodes and update path.
      replaceNodes(getTreeModel(diagramData));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      diagramData.DataDescription.Name,
      diagramData.Id,
      diagramData.DataDescription.FullTypeName,
      diagramData.DataDescription.IsList,
    ]);

    const handleNameChanged = useCallback(
      (newName: string) => {
        onNameChanged(diagramData.Id, newName);
      },
      [diagramData, onNameChanged]
    );

    const handleClick = useCallback(() => onClick(diagramData), [diagramData, onClick]);

    const handleMouseDown = useCallback(
      (e: React.MouseEvent<HTMLElement>) => onMouseDown({ ...e, diagram: diagramData }),
      [diagramData, onMouseDown]
    );

    const { Width: width, Height: height, Top: top, Left: left, ZIndex: zIndex } = diagramData.View;

    const isRootNodeExpanded = nodes.find(item => item.data!.isRoot)!.isExpanded;

    return (
      <div
        className={getClassNames(diagramData)}
        onMouseDown={handleMouseDown}
        data-testid={diagramData.Id}
        style={{ width, height: isRootNodeExpanded ? height : SimpleDiagramHeight, top, left, zIndex }}
      >
        {diagramData.ValidationIssues && diagramData.ValidationIssues.length > 0 && (
          <ValidationIcon className="dp-data-diagram__validation" validationIssues={diagramData.ValidationIssues} />
        )}
        <div className={'dp-data-diagram__content'}>
          <Tree
            nodes={nodes}
            onNodeSelect={handleClick}
            onNodeExpand={expandNode}
            onNodeFold={foldNode}
            nodeContentComponent={(props: React.PropsWithChildren<ITreeNodeContentProps<IDiagramTreeNode>>) => (
              <DataDiagramField node={props} onNameChanged={handleNameChanged} />
            )}
            classes={{ node: 'dp-data-diagram-field' }}
          />
        </div>
      </div>
    );
  }
);

function getRootNode(diagram: DiagramDisplayData): ITreeNode<IDiagramTreeNode> {
  return {
    nodeId: diagram.Id,
    name: diagram.DataDescription.Name,
    data: {
      dataDescription: diagram.DataDescription,
      path: diagram.DataDescription.Name,
      isRoot: true,
    },
    isActive: false,
    description: diagram.DataDescription.FormattedTypeName,
    /**
     * Simple types are collapsed by default to look like simple type.
     * It is needed to not confuse people, because they don't expect to see
     * Int32, DateTime, Decimal, TimeSpan, ... as complex types.
     */
    hasChildren: diagram.DataDescription.HasReadableSubElements,
    isSelectable: true,
    isExpanded: !diagram.IsSimpleType,
  };
}

function getTreeModel(diagram: DiagramDisplayData): ITreeNode<IDiagramTreeNode>[] {
  return [
    {
      ...getRootNode(diagram),
      childNodes: (diagram.SubDataDesriptions ?? []).map(item => convertToNode(item, diagram.DataDescription.Name)),
    },
  ];
}

function convertToNode(dataDescription: DataDescription, parentPath: string): ITreeNode<IDiagramTreeNode> {
  const path = `${parentPath ?? ''}.${dataDescription.Name}`;

  return {
    nodeId: path,
    name: dataDescription.Name,
    data: {
      dataDescription: dataDescription,
      path,
      isRoot: false,
    },
    description: dataDescription.FormattedTypeName,
    isActive: false,
    isSelectable: true,
    hasChildren: dataDescription.HasReadableSubElements,
    isExpanded: false,
    actions: dataDescription.IsList
      ? [{ element: <div className="dp-data-diagram-field__list-icon" />, visibility: TreeNodeActionVisiblity.allways }]
      : undefined,
  };
}
